import { Checkbox, IconButton, MuiAlert, Spin, Tooltip } from '@fox/atoms';
import React from 'react';
import styled from 'styled-components';
import { useResponsiveTableContext } from './context';
import { ResponsiveTableColumn } from './contracts';

const SegmentWrapper = styled.div<{ $isBottom?: boolean; $isRight?: boolean; $isHeader?: boolean; $width?: string | number }>`
	border-left: 1px solid ${props => props.theme.colors.grey?.['200'] ?? 'grey'};
	border-top: 1px solid ${props => props.theme.colors.grey?.['200'] ?? 'grey'};
	height: 3rem;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding-top: 2rem;
	padding-bottom: 2rem;
	background: ${props => (props.$isHeader ? props.theme.colors.grey?.[200] : 'inherit')};
	${props => (props.$isBottom ? `border-bottom: 1px solid ${props.theme.colors.grey?.['200'] ?? 'grey'};` : '')};
	${props => (props.$isRight ? `border-right: 1px solid ${props.theme.colors.grey?.['200'] ?? 'grey'};` : '')};
	padding-left: 1rem;
	padding-right: 1rem;
	${props => (props.$width ? `width: ${props.$width};` : '')}
`;

const ColumnTextWrapper = styled.div`
	max-width: 500px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const DesktopViewWrapper = styled.div`
	display: flex;
	flex-direction: row;
	overflow-x: auto;
`;

function Column<T>({ column, data, isColumnOnTheRight }: { column: ResponsiveTableColumn<T>; data: T[]; isColumnOnTheRight: boolean }) {
	const { sortProps, rowActionComponent, clearRowSelection, disableTooltips } = useResponsiveTableContext();
	return (
		<>
			<div style={{ minWidth: 'max-content', width: column.desktopWidth ?? '100%' }}>
				<SegmentWrapper $isHeader $isRight={isColumnOnTheRight} $width={column.desktopWidth}>
					<div>{column.title}</div>
					{column.sortable && sortProps ? (
						<IconButton
							onClick={() => {
								if (column.dataIndex) {
									sortProps.onSort({ column: column.dataIndex, asc: !sortProps.sort?.asc });
									clearRowSelection();
								}
							}}
							iconName={sortProps.sort?.asc === true ? 'ArrowDropUp' : 'ArrowDropDown'}
						/>
					) : null}
				</SegmentWrapper>
				{data?.map((record, i) => {
					const isRowOnTheBottom = data.length - 1 === i;
					const Component = rowActionComponent?.(record);
					return (
						<React.Fragment key={`${column.dataIndex}_${i}`}>
							<SegmentWrapper
								$isBottom={isRowOnTheBottom}
								$isRight={isColumnOnTheRight}
								className={`row_${i}`}
								$width={column.desktopWidth}
							>
								<div style={{ textAlign: 'center' }}>
									{column.isActionColumn && Component ? (
										Component
									) : column?.render ? (
										column.render(record)
									) : disableTooltips ? (
										<ColumnTextWrapper>{column.dataIndex ? record[column.dataIndex] : ''}</ColumnTextWrapper>
									) : (
										<Tooltip title={column.dataIndex ? record[column.dataIndex] : ''}>
											<ColumnTextWrapper>{column.dataIndex ? record[column.dataIndex] : ''}</ColumnTextWrapper>
										</Tooltip>
									)}
								</div>
							</SegmentWrapper>
						</React.Fragment>
					);
				})}
			</div>
		</>
	);
}

function RowSelectors() {
	const {
		data,
		loading,
		clearRowSelection,
		addRowSelection,
		selectAllRows,
		removeRowSelection,
		selectedRows,
		rowIdentifier,
		rowShowSelectorEvaluatorFunction
	} = useResponsiveTableContext();
	const relevantData = data?.filter(d => rowShowSelectorEvaluatorFunction?.(d) ?? true) ?? [];
	return (
		<div style={{ maxWidth: 'max-content', width: '100%' }}>
			<SegmentWrapper
				$isHeader
				onClick={() => {
					if (selectedRows.length < relevantData.length) {
						selectAllRows();
					} else {
						clearRowSelection();
					}
				}}
			>
				<Checkbox
					disabled={loading}
					color="primary"
					checked={relevantData.length > 0 && selectedRows.length === relevantData.length}
				/>
			</SegmentWrapper>
			{data?.map((d, i) => {
				const isRowOnTheBottom = data.length - 1 === i;

				if (rowShowSelectorEvaluatorFunction?.(d) ?? true) {
					const isChecked = selectedRows.findIndex(r => r === d[rowIdentifier!]) > -1; //without a row indentifier this component will not be rendered anyway
					return (
						<SegmentWrapper
							key={i}
							$isBottom={isRowOnTheBottom}
							onClick={() => {
								if (isChecked) {
									removeRowSelection(d[rowIdentifier!]);
								} else {
									addRowSelection(d[rowIdentifier!]);
								}
							}}
						>
							<Checkbox disabled={loading} checked={isChecked} color="primary" />
						</SegmentWrapper>
					);
				}

				return (
					<SegmentWrapper key={i} $isBottom={isRowOnTheBottom}>
						<></>
					</SegmentWrapper>
				);
			})}
		</div>
	);
}

export function DeskTopView() {
	const { data, columns, loading, rowIdentifier, selectedRowActionComponent, selectedRows } = useResponsiveTableContext();

	const SelectedRowActionComponent = selectedRowActionComponent?.(data, selectedRows);

	return (
		<Spin spinning={loading}>
			{SelectedRowActionComponent ? SelectedRowActionComponent : null}
			<DesktopViewWrapper>
				{rowIdentifier ? <RowSelectors /> : null}
				{columns?.map((column, index) => {
					const isColumnOnTheRight = columns.length - 1 === index;
					return (
						<Column key={`${column.dataIndex}_${index}`} column={column} data={data} isColumnOnTheRight={isColumnOnTheRight} />
					);
				})}
			</DesktopViewWrapper>
			{(data?.length ?? 0) < 1 ? <MuiAlert severity="info">No data to display</MuiAlert> : null}
		</Spin>
	);
}
